import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTableSort } from '../../../common/hooks/useTableSort';
import { useMaterialScopeOptions } from '../../hooks/useMaterialScopeOptions';
import { useMaterialReports } from '../../../hooks/useMaterialReports';
import { useWarehouses } from '../../../hooks/useWarehouses';
import { useMaterialGroups } from '../../../hooks/useMaterialGroups';
import { useProviders } from '../../../hooks/useProviders';
import { REPORT_COLUMNS } from '../../../common/components/materials/materialPresentationConstants';
import {
  CUSTOM_PERIOD,
  DASHBOARD_TABS,
  DEFAULT_PERIOD,
  INITIAL_MATERIAL_DASHBOARD_FILTERS,
  MATERIAL_TEXTS,
  PROJECT_DRILL_TAB,
  PROVIDER_DRILL_TAB,
} from '../constants/materialDashboardConstants';
import { materialPeriod } from '../utils/materialPeriod';
import { usePagination } from '../../../common/hooks/usePagination';
import { getApiErrorMessage } from '../../../utils/getApiErrorMessage';

const GROUPS_PAGE_SIZE = 100;
const FALLBACK_REPORT_VIEW = 'lines';

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  return data?.items || data?.results || [];
};

/**
 * Hook del tablero: filtros globales, pestaña activa (una vista por pestaña) y su tabla.
 * @returns {Object} Datos de la pestaña activa, filtros y handlers para la página.
 */
export const useMaterialDashboard = () => {
  const scopeOptions = useMaterialScopeOptions();
  const [params, setParams] = useSearchParams();
  const tabIndex = Math.max(0, DASHBOARD_TABS.findIndex((tab) => tab.value === params.get('view')));
  const activeTab = DASHBOARD_TABS[tabIndex];
  const [filters, setFilters] = useState(INITIAL_MATERIAL_DASHBOARD_FILTERS);
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [columnFilters, setColumnFilters] = useState([]);
  const { page, setPage, rowsPerPage, handleRowsPerPageChange } = usePagination();
  const [refreshedAt, setRefreshedAt] = useState(null);
  const tableSort = useTableSort(() => setPage(1));
  const reportFilters = refreshedAt ? { ...filters, refreshed_at: refreshedAt } : filters;

  const summary = useMaterialReports('summary', reportFilters);
  const summaryData = summary.isPlaceholderData ? undefined : summary.data;
  const isSummaryLoading = summary.isLoading || summary.isPlaceholderData;
  const quoteId = summaryData?.valuation_quote_id;
  const report = useMaterialReports(
    activeTab.reportView || FALLBACK_REPORT_VIEW,
    {
      ...reportFilters,
      ...tableSort.sortParams,
      valuation_quote_id: quoteId,
      page,
      per_page: rowsPerPage,
      column_filters: columnFilters,
    },
    { enabled: Boolean(activeTab.reportView) && !!quoteId && !summary.isPlaceholderData }
  );
  const reportData = report.data?.valuation_quote_id === quoteId ? report.data : undefined;
  const { data: warehouses = [] } = useWarehouses();
  const { data: groups } = useMaterialGroups({ per_page: GROUPS_PAGE_SIZE });
  const { data: providers = [] } = useProviders();

  const resetTable = () => {
    setPage(1);
    setColumnFilters([]);
    tableSort.resetSort();
  };

  const change = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const selectTab = (index) => {
    setParams({ view: DASHBOARD_TABS[index].value });
    resetTable();
  };

  const goToTab = (value) => selectTab(DASHBOARD_TABS.findIndex((tab) => tab.value === value));

  const changePeriod = (value) => {
    const nextPeriod = value || DEFAULT_PERIOD;
    setPeriod(nextPeriod);
    if (nextPeriod !== CUSTOM_PERIOD) {
      setFilters((prev) => ({ ...prev, ...materialPeriod(nextPeriod) }));
    }
    setPage(1);
  };

  const clear = () => {
    setFilters(INITIAL_MATERIAL_DASHBOARD_FILTERS);
    setPeriod(DEFAULT_PERIOD);
    resetTable();
  };

  const changeColumnFilters = (value) => {
    setColumnFilters(value);
    setPage(1);
  };

  const selectRow = activeTab.selectFilter
    ? (row) => row?.uuid && change(activeTab.selectFilter, row.uuid)
    : undefined;

  const drillProject = (row) => {
    if (!row?.uuid) return;
    change('uuid_project', row.uuid);
    goToTab(PROJECT_DRILL_TAB);
  };

  const drillProvider = (row) => {
    if (!row?.uuid) return;
    const isSelected = filters.uuid_provider_legal_entity === row.uuid;
    change('uuid_provider_legal_entity', isSelected ? '' : row.uuid);
    goToTab(PROVIDER_DRILL_TAB);
  };

  const table = activeTab.reportView
    ? {
        rows: reportData?.items,
        total: reportData?.total,
        totals: reportData?.table_totals,
        columnOptions: reportData?.column_options,
        isLoading: isSummaryLoading || report.isLoading || (!reportData && report.isFetching),
      }
    : {
        rows: [],
        isLoading: isSummaryLoading,
      };

  return {
    activeTab,
    changeColumnFilters,
    changePeriod,
    clear,
    columnFilters,
    drillProject,
    drillProvider,
    errorMessage:
      (summary.isError && getApiErrorMessage(summary.error, MATERIAL_TEXTS.error)) ||
      (report.isError && getApiErrorMessage(report.error, MATERIAL_TEXTS.error)) ||
      '',
    filters,
    groups: groups?.items || [],
    handleFilterChange: change,
    handleRowsPerPageChange,
    rowsPerPage,
    isRefreshing: summary.isFetching || report.isFetching,
    isSummaryLoading,
    page,
    period,
    providers: normalizeList(providers),
    refresh: () => setRefreshedAt(Date.now()),
    retry: () => {
      summary.refetch();
      if (activeTab.reportView) report.refetch();
    },
    scopeOptions,
    selectRow,
    selectTab,
    setPage,
    sort: tableSort.sort,
    sortTable: tableSort.toggleSort,
    summary: summaryData,
    tabIndex,
    table,
    tableColumns: REPORT_COLUMNS[activeTab.columns] || [],
    warehouses,
  };
};
